import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { login, signup, verifyEmail, forgotPassword, resetPassword, logout, getCompanyProfile as getCompanyProfileService, updateCompanyProfile as updateCompanyProfileService, uploadProfileImage as uploadProfileImageService } from '../company/services/companyAuthService';

// Load initial state from localStorage
const loadInitialState = () => {
  const token = localStorage.getItem('token');
  const companyData = localStorage.getItem('company');
  return {
    company: companyData ? JSON.parse(companyData) : null,
    isAuthenticated: !!token,
    loading: false,
    error: null,
    verificationStatus: null,
    resetPasswordStatus: null
  };
};

// Async thunks
export const loginCompany = createAsyncThunk(
  'company/login',
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await login(credentials);
      localStorage.setItem('token', response.token);
      localStorage.setItem('company', JSON.stringify(response.company));
      localStorage.setItem('userType', 'company'); 
      return response;
    } catch (error) {
      return rejectWithValue(
        error?.message || 
        error?.error?.message || 
        'Login failed'
      );
    }
  }
);

export const signupCompany = createAsyncThunk(
  'company/signup',
  async (companyData, { rejectWithValue }) => {
    try {
      const response = await signup(companyData);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Signup failed');
    }
  }
);

export const verifyCompanyEmail = createAsyncThunk(
  'company/verifyEmail',
  async (code, { rejectWithValue }) => {
    try {
      const response = await verifyEmail(code);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Verification failed');
    }
  }
);

export const forgotCompanyPassword = createAsyncThunk(
  'company/forgotPassword',
  async (email, { rejectWithValue }) => {
    try {
      const response = await forgotPassword(email);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Password reset request failed');
    }
  }
);
export const resetCompanyPassword = createAsyncThunk(
  'company/resetPassword',
  async ({ token, passwords }, { rejectWithValue }) => {
    try {
      const response = await resetPassword(token, passwords);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Password reset failed');
    }
  }
);

export const fetchCompanyProfile = createAsyncThunk(
  'company/fetchProfile',
  async (_, { rejectWithValue }) => {
    try {
      const response = await getCompanyProfileService();
      // Update company data in localStorage with complete data
      const companyData = {
        ...response.company,
        firstName: response.company.firstName || response.company.companyName?.split('.')[0] || "",
        lastName: response.company.lastName || "",
        email: response.company.email || "",
        companyName: response.company.companyName || "",
        companyWebsite: response.company.companyWebsite || "",
      };
      localStorage.setItem('company', JSON.stringify(companyData));
      return companyData;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch profile');
    }
  }
);

// Add a new thunk for initial company profile fetch
export const fetchInitialCompanyProfile = createAsyncThunk(
  'company/fetchInitialProfile',
  async (_, { dispatch }) => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        await dispatch(fetchCompanyProfile()).unwrap();
      } catch (error) {
        console.error('Failed to fetch initial company profile:', error);
      }
    }
  }
);

// export const checkCompanyAuth = createAsyncThunk(
//   'company/checkAuth',
//   async (_, { rejectWithValue }) => {
//     try {
//       const response = await checkAuth();
//       return response;
//     } catch (error) {
//       return rejectWithValue(error.response?.data || 'Auth check failed');
//     }
//   }
// );

export const logoutCompany = createAsyncThunk(
  'company/logout',
  async (_, { rejectWithValue }) => {
    try {
      await logout();
      // Clear localStorage
      localStorage.removeItem('token');
      localStorage.removeItem('company');
      localStorage.removeItem('userType');
      return null;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Logout failed');
    }
  }
);

export const updateCompanyProfile = createAsyncThunk(
  'company/updateProfile',
  async (profileData, { rejectWithValue }) => {
    try {
      const response = await updateCompanyProfileService(profileData);
      // Update company data in localStorage
      localStorage.setItem('company', JSON.stringify(response.company));
      return response.company;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update profile');
    }
  }
);

// Add new thunk for profile image upload
export const uploadCompanyProfileImage = createAsyncThunk(
  'company/uploadProfileImage',
  async (imageData, { rejectWithValue }) => {
    try {
      const response = await uploadProfileImageService(imageData);
      return response.profileImage;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to upload profile image');
    }
  }
);

const companySlice = createSlice({
  name: 'company',
  initialState: loadInitialState(),
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearStatus: (state) => {
      state.verificationStatus = null;
      state.resetPasswordStatus = null;
    },
    clearCompanyDetails: (state) => {
      state.company = null;
      state.isAuthenticated = false;
      state.error = null;
      // Clear localStorage
      localStorage.removeItem('token');
      localStorage.removeItem('company');
    }
  },
  extraReducers: (builder) => {
    builder
      // Login cases
      .addCase(loginCompany.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginCompany.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.company = action.payload.company;
        state.error = null;
        localStorage.setItem('userType', 'company');
      })
      .addCase(loginCompany.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Signup cases
      .addCase(signupCompany.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(signupCompany.fulfilled, (state, action) => {
        state.loading = false;
        state.company = action.payload.company;
        state.isAuthenticated = true; // FIX: Considered logged in after signup
        state.error = null;
        localStorage.setItem('userType', 'company');
      })
      .addCase(signupCompany.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        localStorage.removeItem('userType');
      })

      // Email verification cases
      .addCase(verifyCompanyEmail.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(verifyCompanyEmail.fulfilled, (state, action) => {
        state.loading = false;
        state.verificationStatus = 'success';
        state.company = action.payload.company;
      })
      .addCase(verifyCompanyEmail.rejected, (state, action) => {
        state.loading = false;
        state.verificationStatus = 'failed';
        state.error = action.payload;
        localStorage.removeItem('userType');
      })

      // Forgot password cases
      .addCase(forgotCompanyPassword.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(forgotCompanyPassword.fulfilled, (state) => {
        state.loading = false;
        state.resetPasswordStatus = 'emailSent';
      })
      .addCase(forgotCompanyPassword.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        localStorage.removeItem('userType');
      })

      // Reset password cases
      .addCase(resetCompanyPassword.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(resetCompanyPassword.fulfilled, (state) => {
        state.loading = false;
        state.resetPasswordStatus = 'success';
      })
      .addCase(resetCompanyPassword.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        localStorage.removeItem('userType');
      })

      // Fetch company profile cases
      .addCase(fetchCompanyProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCompanyProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.company = action.payload;
        state.isAuthenticated = true;
        state.error = null;
      })
      .addCase(fetchCompanyProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.isAuthenticated = false;
      })

      // Logout cases
      .addCase(logoutCompany.fulfilled, (state) => {
        state.company = null;
        state.isAuthenticated = false;
        state.error = null;
        localStorage.removeItem('userType');
      })

      // Update company profile cases
      .addCase(updateCompanyProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateCompanyProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.company = action.payload;
        state.error = null;
      })
      .addCase(updateCompanyProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Profile image upload cases
      .addCase(uploadCompanyProfileImage.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(uploadCompanyProfileImage.fulfilled, (state, action) => {
        state.loading = false;
        if (state.company) {
          state.company.profileImage = action.payload;
        }
        state.error = null;
      })
      .addCase(uploadCompanyProfileImage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  }
});

export const { clearError, clearStatus, clearCompanyDetails } = companySlice.actions;
export default companySlice.reducer;
